import { motion } from "framer-motion";
import { Check, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  {
    name: "Stage 1 Remap",
    tag: "OEM Hardware Compatible",
    price: "£200",
    featured: true,
    features: [
      "100% stock hardware friendly",
      "+20–35% power increase",
      "Speed limiter removal",
      "Fuel economy optimisation",
      "1-year map warranty",
    ],
  },
  {
    name: "EGR Delete",
    tag: "Off-Road / Motorsport",
    price: "£150",
    features: [
      "EGR valve software deactivation",
      "Prevents inlet carbon build-up",
      "Restores power & throttle response",
      "Eliminates EGR-related limp mode",
      "Cleaner intake, healthier engine",
    ],
  },
  {
    name: "AdBlue Delete",
    tag: "Off-Road / Motorsport",
    price: "£150",
    features: [
      "Full AdBlue / SCR deactivation",
      "No more AdBlue warnings or countdowns",
      "Eliminates NOx sensor faults",
      "No pump or crystallisation failures",
      "Fault-free dashboard, guaranteed",
    ],
  },
  {
    name: "DPF Delete",
    tag: "Off-Road / Motorsport",
    price: "£150",
    features: [
      "DPF software removal",
      "No more forced regenerations",
      "Restores power & fuel economy",
      "Eliminates blocked-DPF limp mode",
      "Ideal for high-mileage diesels",
    ],
  },
];

export const Stages = ({ onBook }) => (
  <section id="stages" className="relative py-24 sm:py-32 border-t border-[#1F232D] carbon-grid">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14"
      >
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-nitro mb-4 flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-nitro" />
          Tuning & Solutions
        </p>
        <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl leading-[0.95] text-neutral-100">
          Choose your <span className="text-nitro">service</span>
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.name}
            data-testid="tuning-stage-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -8 }}
            className={`relative rounded-2xl p-8 flex flex-col transition-shadow duration-300 ${
              s.featured
                ? "bg-panel border border-nitro shadow-[0_0_40px_rgba(255,46,77,0.25)]"
                : "bg-panel/70 border border-[#262933] hover:border-nitro/50"
            }`}
          >
            {s.featured && (
              <span className="absolute -top-3 left-8 bg-nitro text-white font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full flex items-center gap-1">
                <Flame size={11} /> Most Popular
              </span>
            )}
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">
              {s.tag}
            </p>
            <h3 className="font-heading font-bold uppercase text-2xl text-neutral-100 mt-2">
              {s.name}
            </h3>
            <p className="font-heading font-extrabold text-5xl text-nitro mt-5">
              {s.price}
              {s.price !== "Custom" && (
                <span className="text-sm text-neutral-500 font-body font-normal tracking-normal">
                  {" "}
                  / incl. VAT
                </span>
              )}
            </p>
            <ul className="mt-7 flex flex-col gap-3 flex-1">
              {s.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                  <Check size={16} className="text-nitro shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`stage-book-${i + 1}`}
              onClick={() => onBook(s.name)}
              className={`mt-8 w-full font-heading font-semibold uppercase tracking-widest rounded-full py-6 transition-all duration-300 ${
                s.featured
                  ? "bg-nitro hover:bg-[#ff4561] text-white hover:shadow-[0_0_25px_rgba(255,46,77,0.5)]"
                  : "bg-transparent border border-[#262933] hover:border-nitro text-neutral-100 hover:bg-nitro/10"
              }`}
              variant={s.featured ? "default" : "outline"}
            >
              Book {s.name}
            </Button>
          </motion.div>
        ))}
      </div>

      <p className="mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600 text-center">
        EGR, AdBlue & DPF delete services are for off-road and motorsport use only
      </p>
    </div>
  </section>
);
